import { Tab } from "@headlessui/react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { isHiddenFieldType } from "../../../constants/fieldTypes";
import appleWalletEn from "../../../assets/wallet/ios/en.svg";
import appleWalletEs from "../../../assets/wallet/ios/es.svg";
import appleWalletTr from "../../../assets/wallet/ios/tr.svg";
import googleWalletEn from "../../../assets/wallet/android/en.png";
import googleWalletEs from "../../../assets/wallet/android/es.png";
import googleWalletTr from "../../../assets/wallet/android/tr.png";

const COUNTRY_CODES = [
  { code: "+1", country: "US/CA" },
  { code: "+44", country: "UK" },
  { code: "+90", country: "TR" },
  { code: "+34", country: "ES" },
];

const formOrder = ["name", "email", "phone", "birthday"];

export default function FormPreview({
  selectedStore,
  formData,
  description,
  fields,
  submitText,
  handleFieldChange,
  formT, // the form's own language; only the edit page has one
}) {
  // The form's own language when the caller supplies it (edit page), else
  // the merchant's dashboard language (create page, which has no form language
  // to render in yet).
  const { t } = useLanguage();
  const translationFunction = formT || t;

  const formFields = [
    ...formOrder
      .map((orderedField) =>
        fields.find((field) => field.field === orderedField),
      )
      .filter((field) => field), // Remove undefined entries
    ...fields.filter((field) => !formOrder.includes(field.field)),
    // Hidden fields are left out so the preview shows what the visitor
    // actually sees: their value arrives in the URL, with no input to fill.
  ].filter((field) => !isHiddenFieldType(field.type));

  return (
    <Tab.Group>
      <Tab.List className="flex space-x-3 mb-6">
        <Tab
          className={({ selected }) =>
            `w-full rounded-lg py-2.5 text-sm font-medium leading-5 border border-gray-200 
              ${
                selected
                  ? "bg-white text-indigo-600 shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-white/12 hover:text-gray-800"
              }`
          }
        >
          {translationFunction("forms.edit.preview.formTab")}
        </Tab>
        <Tab
          className={({ selected }) =>
            `w-full rounded-lg py-2.5 text-sm font-medium leading-5 border border-gray-200
              ${
                selected
                  ? "bg-white text-indigo-600 shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-50 hover:text-gray-800"
              }`
          }
        >
          {translationFunction("forms.edit.preview.successTab")}
        </Tab>
      </Tab.List>

      <Tab.Panels>
        {/* Form Preview Panel */}
        <Tab.Panel
          className="max-w-md mx-auto p-8 preview-form-container"
          style={{ backgroundColor: formData.backgroundColor }}
        >
          <div className="space-y-6">
            {/* Company Logo and Title */}
            <div className="flex items-center justify-center mb-6">
              {selectedStore?.logo_url && (
                <img
                  src={selectedStore.logo_url}
                  alt={selectedStore.name}
                  className="h-32 w-auto mb-4"
                />
              )}
            </div>

            {/* Form Description */}
            {description && (
              <div
                className="text-center mb-8 rich-content"
                style={{ color: formData.textColor, lineHeight: "1.15" }}
                dangerouslySetInnerHTML={{ __html: description }}
              />
            )}

            {/* Form Fields */}
            {formFields.map((field, index) => (
              <div key={index} className="space-y-1">
                {field.field != "marketing" && (
                  <label
                    className="block text-sm font-medium"
                    style={{ color: formData.textColor }}
                  >
                    {field.label || "Untitled Field"}{" "}
                    {field.required && <span className="text-red-500">*</span>}
                  </label>
                )}
                {field.type === "text" && (
                  <input
                    type="text"
                    placeholder="Enter text"
                    className="block w-full rounded-lg border-gray-300 shadow-xs bg-gray-50"
                    disabled
                  />
                )}
                {field.type === "date" && (
                  <input
                    type="date"
                    className="block w-full rounded-lg border-gray-300 shadow-xs bg-gray-50"
                    disabled
                  />
                )}
                {field.type === "email" && (
                  <input
                    type="email"
                    placeholder="email@example.com"
                    className="block w-full rounded-lg border-gray-300 shadow-xs bg-gray-50"
                    disabled
                  />
                )}
                {field.type === "tel" && (
                  <div className="flex gap-2">
                    <select
                      disabled
                      value={field.countryCode || "+1"}
                      onChange={(e) =>
                        handleFieldChange(index, {
                          ...field,
                          countryCode: e.target.value,
                        })
                      }
                      className="rounded-lg border-gray-300 shadow-xs w-32"
                    >
                      {COUNTRY_CODES.map(({ code, country }) => (
                        <option key={code} value={code}>
                          {code} {country}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      onChange={(e) =>
                        handleFieldChange(index, {
                          ...field,
                          placeholder: e.target.value,
                        })
                      }
                      placeholder="(555) 000-0000"
                      className="block w-full rounded-lg border-gray-300 shadow-xs bg-gray-50"
                      disabled
                    />
                  </div>
                )}
                {field.type === "select" && (
                  <select
                    disabled
                    className="block w-full rounded-lg border-gray-300 shadow-xs bg-gray-50"
                  >
                    <option key="selectPlaceholder" value="">
                      {translationFunction(
                        "forms.new.fields.options.selectPlaceholder",
                      )}
                    </option>
                    {field.options?.map((option, index) => (
                      <option key={`${field.label}-${index}`} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                )}
                {field.type === "radio" && (
                  <div className="space-y-2">
                    {field.options?.map((option, index) => (
                      <div
                        key={`${field.label}-${index}`}
                        className="flex items-center"
                      >
                        <input
                          type="radio"
                          name={`preview-${field.label}`}
                          disabled
                          className="h-4 w-4 border-gray-300"
                          style={{ accentColor: formData.textColor }}
                        />
                        <label
                          className="ml-2 block text-sm"
                          style={{ color: formData.textColor }}
                        >
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                {field.type === "checkbox" && field.field != "marketing" && (
                  <div className="space-y-2">
                    {field.options?.map((option, index) => (
                      <div
                        key={`${field.label}-${index}`}
                        className="flex items-center"
                      >
                        <input
                          type="checkbox"
                          disabled
                          className="h-4 w-4 rounded-sm border-gray-300"
                          style={{ accentColor: formData.textColor }}
                        />
                        <label
                          className="ml-2 block text-sm"
                          style={{ color: formData.textColor }}
                        >
                          {option}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                {field.description && (
                  <p
                    className="text-sm mt-1"
                    style={{ color: formData.textColor, opacity: 0.7 }}
                  >
                    {field.description}
                  </p>
                )}
              </div>
            ))}

            {/* Marketing Checkbox */}
            {fields.find((field) => field.field === "marketing") && (
              <div className="space-y-1">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      type="checkbox"
                      disabled
                      className="h-4 w-4 rounded-sm border-gray-300"
                      style={{ accentColor: formData.textColor }}
                    />
                  </div>
                  <div className="ml-3">
                    <div
                      className="text-sm"
                      style={{ color: formData.textColor }}
                      dangerouslySetInnerHTML={{
                        __html: fields.find((f) => f.field === "marketing")
                          .options[0],
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Terms of Service Checkbox */}
            <div className="space-y-1">
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    type="checkbox"
                    disabled
                    className="h-4 w-4 rounded-sm border-gray-300"
                    style={{ accentColor: formData.textColor }}
                  />
                </div>
                <div className="ml-3">
                  <div
                    className="text-sm"
                    style={{ color: formData.textColor }}
                  >
                    {translationFunction("forms.tosAgreement", {
                      termsOfUseLink: (
                        <a
                          className="font-medium underline"
                          style={{ color: formData.textColor, opacity: 0.85 }}
                        >
                          {translationFunction(
                            "passes.form.termsOfService.label",
                          )}
                        </a>
                      ),
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6">
              <button
                type="button"
                disabled
                className="w-full inline-flex justify-center py-2.5 px-4 border border-transparent shadow-xs text-sm font-medium rounded-lg"
                style={{
                  backgroundColor: formData.textColor,
                  color: formData.backgroundColor,
                }}
              >
                {submitText}
              </button>
            </div>
          </div>
        </Tab.Panel>

        {/* Success Preview Panel */}
        <Tab.Panel
          className="shadow-xs p-8 max-w-md mx-auto"
          style={{ backgroundColor: formData.backgroundColor }}
        >
          <div className="space-y-6">
            {/* Company Logo */}
            <div className="flex items-center justify-center">
              {selectedStore?.logo_url && (
                <img
                  src={selectedStore.logo_url}
                  alt={selectedStore.name}
                  className="h-32 w-auto mb-4"
                />
              )}
            </div>

            <div className="text-center">
              <h3
                className="text-lg font-medium"
                style={{ color: formData.textColor }}
              >
                {translationFunction("forms.edit.preview.success.title")}
              </h3>
              <p className="mt-2 text-sm" style={{ color: formData.textColor }}>
                {translationFunction(
                  "forms.new.preview.success.description_two",
                )}
              </p>
            </div>

            {/* Wallet Buttons */}
            <div className="pt-4">
              <div
                className="text-sm text-center mb-3"
                style={{ color: formData.textColor }}
              >
                {translationFunction("forms.new.preview.success.description")}
              </div>
              <div className="flex items-center justify-center gap-4">
                {/* iOS Wallet Button */}
                <picture>
                  <source
                    srcSet={appleWalletEs}
                    media="(prefers-language: es)"
                  />
                  <source
                    srcSet={appleWalletTr}
                    media="(prefers-language: tr)"
                  />
                  <img
                    src={appleWalletEn}
                    alt="Add to Apple Wallet"
                    className="h-12 cursor-pointer hover:opacity-80 transition-opacity"
                  />
                </picture>

                {/* Android Wallet Button */}
                <picture>
                  <source
                    srcSet={googleWalletEs}
                    media="(prefers-language: es)"
                  />
                  <source
                    srcSet={googleWalletTr}
                    media="(prefers-language: tr)"
                  />
                  <img
                    src={googleWalletEn}
                    alt="Add to Google Wallet"
                    className="h-12 cursor-pointer hover:opacity-80 transition-opacity"
                  />
                </picture>
              </div>
            </div>
          </div>
        </Tab.Panel>
      </Tab.Panels>
    </Tab.Group>
  );
}
